import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import * as RechartsPrimitive from "recharts";
import type { LegendPayload, TooltipContentProps } from "recharts";
import { cn } from "@shared-utils/ui";

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = {
  light: "",
  dark: ".dark"
} as const;
export type ChartConfig = { [k in string]: {
  label?: React.ReactNode;
  icon?: React.ComponentType;
} & ({
  color?: string;
  theme?: never;
} | {
  color?: never;
  theme: Record<keyof typeof THEMES, string>;
}) };
type ChartContextProps = {
  config: ChartConfig;
};
const ChartContext = React.createContext<ChartContextProps | null>(null);
function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}
const ChartContainer = React.forwardRef<HTMLDivElement, React.ComponentProps<"div"> & {
  config: ChartConfig;
  children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>["children"];
}>((t0, ref) => {
  const $ = _c(25);
  let children;
  let className;
  let config;
  let id;
  let props;
  if ($[0] !== t0) {
    ({
      id,
      className,
      children,
      config,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = config;
    $[4] = id;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    config = $[3];
    id = $[4];
    props = $[5];
  }
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;
  let t1;
  if ($[6] !== config) {
    t1 = {
      config
    };
    $[6] = config;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== className) {
    t2 = cn("[&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline-hidden [&_.recharts-surface]:outline-hidden flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector[stroke='#fff']]:stroke-transparent", className);
    $[8] = className;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== chartId || $[11] !== config) {
    t3 = <ChartStyle id={chartId} config={config} />;
    $[10] = chartId;
    $[11] = config;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== children) {
    t4 = <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>;
    $[13] = children;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== chartId || $[16] !== props || $[17] !== ref || $[18] !== t2 || $[19] !== t3 || $[20] !== t4) {
    t5 = <div data-chart={chartId} ref={ref} className={t2} {...props}>{t3}{t4}</div>;
    $[15] = chartId;
    $[16] = props;
    $[17] = ref;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t1 || $[23] !== t5) {
    t6 = <ChartContext.Provider value={t1}>{t5}</ChartContext.Provider>;
    $[22] = t1;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
});
ChartContainer.displayName = "Chart";
const ChartStyle = t0 => {
  const $ = _c(8);
  const {
    id,
    config
  } = t0;
  let t1;
  if ($[0] !== config) {
    t1 = Object.entries(config).filter(_temp);
    $[0] = config;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const colorConfig = t1;
  if (!colorConfig.length) {
    return null;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Object.entries(THEMES);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== colorConfig || $[4] !== id) {
    t3 = t2.map(t4 => {
      const [theme, prefix] = t4;
      return `
${prefix} [data-chart=${id}] {
${colorConfig.map(t5 => {
        const [key, itemConfig] = t5;
        const color = itemConfig.theme?.[theme as keyof typeof itemConfig.theme] || itemConfig.color;
        return color ? `  --color-${key}: ${color};` : null;
      }).join("\n")}
}
`;
    });
    $[3] = colorConfig;
    $[4] = id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = t3.join("\n");
  let t5;
  if ($[6] !== t4) {
    t5 = <style dangerouslySetInnerHTML={{
      __html: t4
    }} />;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
const ChartTooltip = RechartsPrimitive.Tooltip;
type ChartTooltipContentProps = Omit<Partial<TooltipContentProps>, "labelFormatter"> & React.ComponentProps<"div"> & {
  hideLabel?: boolean;
  hideIndicator?: boolean;
  indicator?: "line" | "dot" | "dashed";
  nameKey?: string;
  labelKey?: string;
  itemsContainerClassName?: string;
  labelFormatter?: (...args: any[]) => React.ReactNode;
};
const ChartTooltipContent = React.forwardRef<HTMLDivElement, ChartTooltipContentProps>(({
  active,
  payload,
  className,
  indicator = "dot",
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey,
  itemsContainerClassName
}, ref) => {
  const {
    config
  } = useChart();
  const tooltipLabel = React.useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null;
    }
    const [item] = payload;
    // The next line is manually modified due to https://github.com/shadcn-ui/ui/issues/4218
    const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value = !labelKey && typeof label === "string" ? config[label]?.label || label : itemConfig?.label;
    if (labelFormatter) {
      return <div className={cn("font-medium", labelClassName)}>
            {labelFormatter(value, payload)}
          </div>;
    }
    if (!value) {
      return null;
    }
    return <div className={cn("font-medium", labelClassName)}>{value}</div>;
  }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);
  if (!active || !payload?.length) {
    return null;
  }
  const nestLabel = payload.length === 1 && indicator !== "dot";
  return <div ref={ref} className={cn("border-border/50 grid min-w-32 items-start gap-1.5 rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-xl", className)}>
        {!nestLabel ? tooltipLabel : null}
        <div className={cn("grid gap-1.5", itemsContainerClassName)}>
          {payload.map((item_0, index) => {
        // The next line is manually modified due to https://github.com/shadcn-ui/ui/issues/4218
        const key_0 = `${nameKey || item_0?.name || item_0?.dataKey || "value"}`;
        const itemConfig_0 = getPayloadConfigFromPayload(config, item_0, key_0);
        const indicatorColor = color || (item_0.payload as {
          fill?: string;
        })?.fill || item_0.color;
        return <div key={typeof item_0.dataKey === "string" ? item_0.dataKey : index} className={cn("flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground", indicator === "dot" && "items-center")}>
                {formatter && item_0?.value !== undefined && item_0.name ?
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          formatter(item_0.value, item_0.name, item_0, index, item_0.payload) : <>
                    {itemConfig_0?.icon ? <itemConfig_0.icon /> : !hideIndicator && <div className={cn("border-(--color-border) bg-(--color-bg) shrink-0 rounded-[2px]", {
              "h-2.5 w-2.5": indicator === "dot",
              "w-1": indicator === "line",
              "w-0 border-[1.5px] border-dashed bg-transparent": indicator === "dashed",
              "my-0.5": nestLabel && indicator === "dashed"
            })} style={{
              "--color-bg": indicatorColor,
              "--color-border": indicatorColor
            } as React.CSSProperties} />}
                    <div className={cn("flex flex-1 justify-between leading-none", nestLabel ? "items-end" : "items-center")}>
                      <div className="grid gap-1.5">
                        {nestLabel ? tooltipLabel : null}
                        <span className="text-muted-foreground">
                          {itemConfig_0?.label || item_0.name}
                        </span>
                      </div>
                      {item_0.value && <span className="font-mono font-medium tabular-nums text-foreground">
                          {item_0.value.toLocaleString()}
                        </span>}
                    </div>
                  </>}
              </div>;
      })}
        </div>
      </div>;
});
ChartTooltipContent.displayName = "ChartTooltip";
const ChartLegend = RechartsPrimitive.Legend;
const ChartLegendContent = React.forwardRef<HTMLDivElement, React.ComponentProps<"div"> & {
  payload?: ReadonlyArray<LegendPayload>;
  verticalAlign?: RechartsPrimitive.LegendProps["verticalAlign"];
  hideIcon?: boolean;
  nameKey?: string;
}>((t0, ref) => {
  const $ = _c(16);
  const {
    className,
    hideIcon: t1,
    payload,
    verticalAlign: t2,
    nameKey
  } = t0;
  const hideIcon = t1 === undefined ? false : t1;
  const verticalAlign = t2 === undefined ? "bottom" : t2;
  const {
    config
  } = useChart();
  if (!payload?.length) {
    return null;
  }
  const t3 = verticalAlign === "top" ? "pb-3" : "pt-3";
  let t4;
  if ($[0] !== className || $[1] !== t3) {
    t4 = cn("flex items-center justify-center gap-4", t3, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== config || $[4] !== hideIcon || $[5] !== nameKey || $[6] !== payload) {
    let t6;
    if ($[8] !== config || $[9] !== hideIcon || $[10] !== nameKey) {
      t6 = item => {
        const key = `${nameKey || (typeof item.dataKey === "string" ? item.dataKey : "") || "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        return <div key={item.value as string} className={cn("flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground")}>{itemConfig?.icon && !hideIcon ? <itemConfig.icon /> : <div className="h-2 w-2 shrink-0 rounded-[2px]" style={{
            backgroundColor: item.color
          }} />}{itemConfig?.label}</div>;
      };
      $[8] = config;
      $[9] = hideIcon;
      $[10] = nameKey;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t5 = payload.map(t6);
    $[3] = config;
    $[4] = hideIcon;
    $[5] = nameKey;
    $[6] = payload;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[12] !== ref || $[13] !== t4 || $[14] !== t5) {
    t6 = <div ref={ref} className={t4}>{t5}</div>;
    $[12] = ref;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
});
ChartLegendContent.displayName = "ChartLegend";

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }
  const payloadPayload = "payload" in payload && typeof payload.payload === "object" && payload.payload !== null ? payload.payload : undefined;
  let configLabelKey: string = key;
  if (key in payload && typeof payload[key as keyof typeof payload] === "string") {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key as keyof typeof payloadPayload] === "string") {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string;
  }
  return configLabelKey in config ? config[configLabelKey] :
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  config[key as keyof typeof config];
}
export { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent, ChartStyle };
function _temp(t0) {
  const [, configItem] = t0;
  return configItem.theme || configItem.color;
}